import type { CSSProperties, FC } from 'react'

import { cn } from '@/lib/utils'

interface HashtagProps {
    tag: string
    tone?: 'paper' | 'ink' | 'ember' | 'outline'
    className?: string
}

const tones = {
    paper: 'border-black/5 bg-paper text-ink shadow-[0_10px_24px_-14px_rgb(30_30_30/0.35)]',
    ink: 'border-transparent bg-ink text-cream',
    ember: 'border-transparent bg-ember text-ink',
    outline: 'border-ink/15 bg-transparent text-ink'
}

export const Hashtag: FC<HashtagProps> = ({ tag, tone = 'paper', className }) => {
    return (
        <span className={cn('inline-flex items-center whitespace-nowrap rounded-full border px-3.5 py-1.5 font-mono text-xs tracking-tight', tones[tone], className)}>
            #{tag.replace(/^#/, '')}
        </span>
    )
}

export type FloatingTag = {
    tag: string
    className: string
    tone?: 'paper' | 'ink' | 'ember' | 'outline'
    duration?: number
    delay?: number
    rotate?: number
}

interface FloatingTagsProps {
    tags: Array<FloatingTag>
    className?: string
}

/**
 * Hashtag chips that drift slowly in place. Purely decorative.
 */
export const FloatingTags: FC<FloatingTagsProps> = ({ tags, className }) => {
    return (
        <div
            aria-hidden='true'
            className={cn('pointer-events-none absolute inset-0', className)}
        >
            {tags.map(item => (
                <span
                    key={item.tag}
                    className={cn('absolute animate-drift', item.className)}
                    style={{
                        animationDuration: `${item.duration ?? 16}s`,
                        animationDelay: `${item.delay ?? 0}s`,
                        rotate: `${item.rotate ?? 0}deg`
                    } as CSSProperties}
                >
                    <Hashtag tag={item.tag} tone={item.tone} />
                </span>
            ))}
        </div>
    )
}
